import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, RotateCcw, Truck } from "lucide-react";
import { api, euro, imgSrc, CATEGORIES } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { SizeAlert } from "@/components/SizeAlert";

export default function ProductDetail() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const wanted = search.get("size") || "";
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [img, setImg] = useState(0);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);
  const { add, setOpen } = useCart();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setP(r.data);
      const pick = wanted && r.data.sizes.find((s) => s.size === wanted);
      const firstColor = pick?.color ?? (r.data.sizes.find((s) => s.color && s.stock > 0)?.color || r.data.sizes[0]?.color || "");
      setColor(firstColor);
      const own = r.data.sizes.filter((s) => s.color === firstColor);
      if (pick) setSize(pick.size);
      else if (own.length === 1 && own[0].stock > 0) setSize(own[0].size);
    }).catch(() => setErr(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (err) return <div className="py-32 text-center" data-testid="product-not-found">Producto no encontrado. <Link to="/tienda" className="underline">Volver a la tienda</Link></div>;
  if (!p) return <div className="py-32 text-center text-slate-500">Cargando…</div>;

  const colors = [...new Set(p.sizes.map((s) => s.color).filter(Boolean))];
  const gallery = (color && p.color_images?.[color]?.length ? p.color_images[color] : p.images) || [];
  const LETTERS = ["XXS", "XS", "S", "M", "L", "XL", "XXL", "2XL", "XXXL", "3XL", "4XL", "TU", "ÚNICA"];
  const sizeNum = (s) => { const n = parseFloat(s.replace(",", ".")); if (!Number.isNaN(n)) return n; const i = LETTERS.indexOf(s.toUpperCase()); return i >= 0 ? 1e6 + i : 1e9; };
  const variants = p.sizes.filter((s) => (s.color || "") === color).sort((a, b) => sizeNum(a.size) - sizeNum(b.size));
  const sel = variants.find((s) => s.size === size);
  const pickColor = (c) => { setColor(c); setSize(""); setQty(1); setImg(0); };
  const onAdd = () => {
    if (!sel) return toast.error("Elige una talla");
    if (qty > sel.stock) return toast.error(`Solo quedan ${sel.stock} unidades`);
    add(p, size, qty, color);
    toast.success("Producto añadido al carrito");
    setOpen(true);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-12 grid grid-cols-1 lg:grid-cols-2 gap-12" data-testid="product-detail-page">
      <div className="space-y-4 min-w-0">
        <div className="aspect-[4/5] bg-white border overflow-hidden">
          <img src={imgSrc(gallery[img], 1200)} alt={p.name} className="w-full h-full object-contain fade-in" key={`${color}-${img}`} data-testid="product-main-image" />
        </div>
        {gallery.length > 1 && (
          <div className="flex gap-3 overflow-x-auto pb-1" data-testid="product-thumbs">
            {gallery.map((u, i) => (
              <button key={u} onClick={() => setImg(i)} className={`shrink-0 w-16 h-20 sm:w-20 sm:h-24 overflow-hidden border-2 ${i === img ? "border-slate-900" : "border-transparent"}`} data-testid={`product-thumb-${i}`}>
                <img src={imgSrc(u, 200)} alt="" loading="lazy" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="lg:pt-6 min-w-0">
        <p className="eyebrow">{p.outlet && <Link to="/outlet" className="mr-2 px-2 py-0.5 bg-verde text-white rounded-sm" data-testid="pdp-outlet-badge">Outlet</Link>}{p.brand} · {CATEGORIES.find((c) => c.id === p.category)?.label}</p>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight leading-none" data-testid="product-name">{p.name}</h1>
        <div className="mt-5 flex items-baseline gap-3">
          <span className="text-3xl font-bold" data-testid="product-price">{euro(p.price)}</span>
          {p.compare_price > p.price && <span className="text-slate-400 line-through">{euro(p.compare_price)}</span>}
          <span className="text-xs text-slate-500">IVA incluido</span>
        </div>
        <p className="mt-6 text-slate-600 leading-relaxed whitespace-pre-line" data-testid="product-description">{p.description}</p>
        {(p.reference || p.outlet_refs?.length > 0) && (
          <p className="mt-3 text-sm text-slate-500" data-testid="product-reference">Referencia: <span className="font-mono font-semibold text-slate-700">{p.reference || p.outlet_refs.join(" · ")}</span></p>
        )}
        {p.features?.length > 0 && (
          <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-slate-700" data-testid="product-features">
            {p.features.map((f) => <li key={f} className="flex gap-2"><span className="text-brand">—</span>{f}</li>)}
          </ul>
        )}
        {colors.length > 0 && (
          <div className="mt-8">
            <p className="filter-title">Color: <span className="text-slate-900">{color}</span></p>
            <div className="flex flex-wrap gap-2">
              {colors.map((c) => {
                const thumb = p.color_images?.[c]?.[0];
                return thumb ? (
                  <button key={c} onClick={() => pickColor(c)} title={c} data-testid={`color-option-${c}`}
                    className={`w-[72px] h-[88px] bg-slate-50 border-2 overflow-hidden transition-colors ${color === c ? "border-brand" : "border-transparent hover:border-slate-300"}`}>
                    <img src={imgSrc(thumb, 200)} alt={c} className="w-full h-full object-contain" />
                  </button>
                ) : (
                  <button key={c} onClick={() => pickColor(c)} data-testid={`color-option-${c}`}
                    className={`h-10 px-4 rounded-full border text-sm font-semibold transition-colors ${color === c ? "bg-brand text-white border-brand" : "border-slate-300 hover:border-brand"}`}>
                    {c}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div className="mt-8">
          <p className="filter-title">Talla</p>
          <div className="flex flex-wrap gap-2">
            {variants.map((s) => (
              <button key={s.size} onClick={() => { setSize(s.size); setQty(1); }} data-testid={`size-option-${s.size}`} title={s.stock === 0 ? "Agotada · pide aviso" : ""}
                className={`min-w-[52px] h-11 px-3 border text-sm font-semibold transition-colors ${s.stock === 0 ? "text-slate-400 line-through decoration-slate-400" : ""} ${size === s.size ? (s.stock === 0 ? "border-verde bg-verde/10 !text-verde" : "bg-brand text-white border-brand") : "border-slate-300 hover:border-brand"}`}>
                {s.size}
              </button>
            ))}
          </div>
          {sel && sel.stock > 0 && <p className={`mt-3 text-xs font-semibold ${sel.stock <= 3 ? "text-amber-600" : "text-emerald-600"}`} data-testid="stock-indicator">{sel.stock <= 3 ? `¡Solo quedan ${sel.stock}!` : "En stock"}</p>}
          {variants.some((s) => s.stock === 0) && !sel && <p className="mt-3 text-xs text-slate-500" data-testid="size-alert-hint">¿Tu talla está tachada? Púlsala y te avisamos cuando llegue.</p>}
        </div>
        {sel && sel.stock === 0 ? <SizeAlert key={`${sel.size}|${color}`} product={p} size={sel.size} color={color} /> : (
        <div className="mt-8 flex gap-3">
          <div className="flex items-center border border-slate-300 h-12">
            <button className="px-3 h-full" onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-decrease"><Minus className="w-4 h-4" /></button>
            <span className="w-8 text-center font-semibold" data-testid="qty-value">{qty}</span>
            <button className="px-3 h-full" onClick={() => setQty(Math.min(20, qty + 1))} data-testid="qty-increase"><Plus className="w-4 h-4" /></button>
          </div>
          <button className="btn-primary flex-1" onClick={onAdd} data-testid="add-to-cart-button">Añadir al carrito</button>
        </div>
        )}
        <div className="mt-10 border-t pt-6 space-y-3 text-sm text-slate-600">
          <p className="flex items-center gap-3"><Truck className="w-4 h-4 text-brand" /> Envío gratis desde 50 € · 24/48 h</p>
          <p className="flex items-center gap-3"><RotateCcw className="w-4 h-4 text-brand" /> {p.outlet ? "Outlet: sin cambios de talla; desistimiento legal 14 días" : "Cambios y devoluciones en 15 días"} · <Link to="/devoluciones" className="underline" data-testid="pdp-returns-link">Ver política</Link></p>
        </div>
      </div>
    </div>
  );
}
